import { COLORS } from "@coeff/utils";
import { ButtonProps, Modal as ModalAD, ModalProps as ModalPropsAd } from "antd";
import * as React from "react";
import styled from "styled-components";

import { Button } from "./Button";
import { DotsLoader } from "./DotsLoader";
import { Loader } from "./Loader";

export type ConfirmBoxProps = React.PropsWithChildren<
  ModalPropsAd & {
    buttonLayout?: "horizontal" | "vertical";
    resetLineHeight?: boolean;
    reverseButtonOrder?: boolean;
    cancelLoading?: boolean;
    onSubmit?: () => void;
    showOk?: boolean;
    showCancel?: boolean;
    showSubmit?: boolean;
    submitDisplayType?: "link" | "button";
    submitLoading?: boolean;
    submitText?: string;
    submitButtonDisabled?: boolean;
    submitButtonStyle?: React.CSSProperties;
    submitButtonProps?: ButtonProps;
  }
>;

const StyledModal = styled(ModalAD)<{
  reverseButtonOrder?: boolean;
  resetLineHeight?: boolean;
}>`
  .ant-modal-body {
    ${({ resetLineHeight }) => (resetLineHeight ? "line-height: 1.3;" : "")};
  }

  .ant-modal-content {
    border-radius: 8px;

    .ant-modal-header {
      padding: 24px 24px 8px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      border-bottom: 0px;

      .ant-modal-title {
        font-size: 18px;
      }
    }

    .ant-modal-body {
      font-size: 16px;
      color: ${COLORS.black45};
      padding: 8px 24px 24px;
    }

    .ant-modal-footer {
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        border-top: none;
        padding: 4px 16px 16px 24px;
      }
    }

  .footer-wrapper {
      display: flex;
      justify-content: space-between;
      flex-direction: row-reverse;
      width: 100%;
      align-items: center;

      ${({ reverseButtonOrder }) => (reverseButtonOrder ? "flex-direction: row;" : "")}

      .btn-wrap {
        &.cancel {
          text-align: right;
        }

        &.ok {
          text-align: left;
        }

        a {
          font-size: 16px;
          font-weight: 500;
          color: ${COLORS.coeblue4};
        }
      }
    }
  }

  &.vertical-layout {
    .ant-modal-content .ant-modal-footer {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 5px 24px 24px;
      text-align: center;

      .btn-wrap {
        a {
          display: block;
          font-size: 14px;
          font-weight: 600;
          line-height: 22px;
          letter-spacing: 0;
          color: ${COLORS.coeblue4};
        }

        &.ok {
          width: 100%;
          border-radius: 4px;
          background-color: ${COLORS.coeblue4};
          border-color: ${COLORS.coeblue4};
          text-align: center;
          margin-bottom: 15px;

          a {
            color: ${COLORS.white};
            padding: 7px 10px;
          }
        }
      }
    }
  }

  &&.redOk {
    .btn-wrap {
      &.ok {
        a {
          color: ${COLORS.red};
        }
      }
    }
  }

  &&.redSubmit {
    .btn-wrap {
      &.submit {
        a {
          color: ${COLORS.red};
        }
      }
    }
  }
`;

export const ConfirmBox: React.FC<ConfirmBoxProps> = ({
  buttonLayout = "horizontal",
  reverseButtonOrder,
  resetLineHeight,
  className,
  children,
  closable = false,
  confirmLoading = false,
  cancelLoading = false,
  showCancel = true,
  showOk = true,
  showSubmit = false,
  submitDisplayType = "link",
  submitLoading = false,
  submitText = "Submit",
  submitButtonDisabled = false,
  maskClosable = false,
  submitButtonStyle,
  submitButtonProps,
  ...props
}) => {
  return (
    <StyledModal
      resetLineHeight={resetLineHeight}
      reverseButtonOrder={reverseButtonOrder}
      {...props}
      maskClosable={maskClosable}
      className={`${className} ${buttonLayout === "vertical" ? "vertical-layout" : ""}`}
      closable={closable}
      width={props.width || 260}
      centered={true}
      footer={
        <div className="footer-wrapper">
          {buttonLayout === "horizontal" && showSubmit && props.onSubmit && (
            <div className="btn-wrap submit">
              {submitDisplayType === "button" ? (
                <Button
                  onClick={!submitLoading ? props.onSubmit : () => {}}
                  type="primary"
                  style={submitButtonStyle}
                  data-testid="modalSubmit"
                  disabled={submitButtonDisabled || submitLoading}
                  {...submitButtonProps}
                >
                  {submitLoading ? <DotsLoader /> : submitText}
                </Button>
              ) : (
                <a onClick={!submitLoading && !submitButtonDisabled ? props.onSubmit : () => {}}>
                  {submitLoading ? <Loader color="blue" /> : submitText}
                </a>
              )}
            </div>
          )}

          {showCancel && props.onCancel && (
            <div className="btn-wrap cancel">
              <a
                onClick={!cancelLoading ? props.onCancel : () => {}}
                style={props.cancelButtonProps?.style}
              >
                {cancelLoading ? (
                  <Loader color={buttonLayout === "vertical" ? "white" : "blue"} />
                ) : (
                  props.cancelText || "Cancel"
                )}
              </a>
            </div>
          )}

          {showOk && props.onOk && (
            <div className="btn-wrap ok">
              <a
                onClick={!confirmLoading ? props.onOk : () => {}}
                style={props.okButtonProps?.style}
              >
                {confirmLoading ? (
                  <Loader color={buttonLayout === "vertical" ? "white" : "blue"} />
                ) : (
                  props.okText || "Ok"
                )}
              </a>
            </div>
          )}
        </div>
      }
    >
      {children}
    </StyledModal>
  );
};
